import { Breadcrumb } from '../Breadcrumb/Breadcrumb';
import { Skeleton } from '../../BasicComponents/Skeleton/Skeleton';
import type { PageContainerProps, PageContainerVariant } from './types';

// ─── Layout constants ─────────────────────────────────────────────────────────

const VARIANT_OUTER: Record<PageContainerVariant, string> = {
  default: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6',
  fluid:   'w-full px-4 sm:px-6 py-4',
  compact: 'w-full px-3 py-3',
};

// ─── Header skeleton ──────────────────────────────────────────────────────────

function HeaderSkeleton({ hasActions }: { hasActions: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6" aria-hidden="true">
      <div className="flex flex-col gap-2 flex-1">
        <Skeleton variant="text" width="40%" height={28} />
        <Skeleton variant="text" width="60%" height={14} />
      </div>
      {hasActions && (
        <div className="shrink-0">
          <Skeleton variant="rect" width={120} height={36} />
        </div>
      )}
    </div>
  );
}

// ─── PageContainer ────────────────────────────────────────────────────────────

export function PageContainer({
  title,
  subtitle,
  breadcrumbs,
  actions,
  variant = 'default',
  loading = false,
  children,
}: PageContainerProps) {
  const hasHeader = loading || title || actions;
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;

  return (
    <div className={VARIANT_OUTER[variant]}>
      {/* Breadcrumbs */}
      {hasBreadcrumbs && !loading && (
        <div className="mb-3">
          <Breadcrumb items={breadcrumbs!} />
        </div>
      )}

      {/* Page header */}
      {hasHeader && (
        loading ? (
          <HeaderSkeleton hasActions={Boolean(actions)} />
        ) : (
          <div className="flex items-start justify-between gap-4 mb-6">
            {/* Title + subtitle */}
            <div className="min-w-0">
              {title && (
                <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Actions slot */}
            {actions && (
              <div className="shrink-0 flex items-center gap-2">
                {actions}
              </div>
            )}
          </div>
        )
      )}

      {/* Page content */}
      {children}
    </div>
  );
}
